import { FLOWCHART_VISUAL_DOT_PITCH } from '../../FlowchartView/components/FlowchartCanvas/constants';

const DOT_RADIUS = 0.85;
const DOT_ALPHA = 0.125;
// Below this spacing the dots are smaller than a pixel and clump into squares.
const FADE_START = 14;
const FADE_END = 8;

/** Where the repeating dots sit for this camera. Kept within one tile so a far pan stays cheap. */
export function dotPaint(camera) {
  const zoom = Number.isFinite(camera?.zoom) && camera.zoom > 0 ? camera.zoom : 1;
  const x = Number.isFinite(camera?.x) ? camera.x : 0;
  const y = Number.isFinite(camera?.y) ? camera.y : 0;
  const rawPitch = FLOWCHART_VISUAL_DOT_PITCH * zoom;
  // A fractional tile beats against the screen pixels and turns into blocks.
  const pitch = rawPitch < 12 ? Math.max(2, Math.round(rawPitch)) : rawPitch;
  const wrap = (value) => ((value % pitch) + pitch) % pitch;
  let alpha = DOT_ALPHA;
  if (pitch <= FADE_END) alpha = 0;
  else if (pitch < FADE_START) alpha = DOT_ALPHA * ((pitch - FADE_END) / (FADE_START - FADE_END));
  const radius = Math.max(DOT_RADIUS, DOT_RADIUS * zoom);
  const solid = Math.min(radius * 0.45, pitch * 0.3);
  const edge = Math.min(radius, pitch * 0.45);
  const image = alpha === 0
    ? 'none'
    : `radial-gradient(circle at center, rgba(255,255,255,${alpha.toFixed(3)}) ${solid.toFixed(2)}px, transparent ${edge.toFixed(2)}px)`;
  return { pitch, x: wrap(x), y: wrap(y), image };
}

export function paintDots(dots, camera) {
  if (!dots) return;
  const paint = dotPaint(camera);
  if (dots.dataset.dotImage !== paint.image) {
    dots.dataset.dotImage = paint.image;
    dots.style.backgroundImage = paint.image;
  }
  dots.style.backgroundSize = `${paint.pitch}px ${paint.pitch}px`;
  dots.style.backgroundPosition = `${paint.x}px ${paint.y}px`;
}

function cameraTransform(camera) {
  return `translate(${camera.x}px, ${camera.y}px) scale(${camera.zoom})`;
}

function wireFrameOf(wires) {
  const minX = Number(wires.dataset.wireMinX);
  const minY = Number(wires.dataset.wireMinY);
  const w = Number(wires.dataset.wireW);
  const h = Number(wires.dataset.wireH);
  if (!Number.isFinite(minX) || !Number.isFinite(minY) || !(w > 0) || !(h > 0)) return null;
  return { minX, minY, w, h };
}

/**
 * Draw the lines at the size they are on screen, then only slide that picture.
 * A camera scale on a full-size picture turns a 1px stroke into a broken
 * bitmap once you zoom out. Resizing the picture (bake) is for when the
 * zoom settles, not for every tick of the wheel.
 */
export function placeWires(wires, camera, { bake = false } = {}) {
  if (!wires || !camera) return;
  const frame = wireFrameOf(wires);
  if (!frame) {
    wires.style.transform = cameraTransform(camera);
    return;
  }
  const zoom = camera.zoom || 1;
  const key = `${frame.minX}|${frame.minY}|${frame.w}|${frame.h}`;
  if (bake || !wires.dataset.bakedZoom || wires.dataset.bakedKey !== key) {
    wires.dataset.bakedZoom = String(zoom);
    wires.dataset.bakedKey = key;
    wires.style.left = `${frame.minX * zoom}px`;
    wires.style.top = `${frame.minY * zoom}px`;
    wires.style.width = `${frame.w * zoom}px`;
    wires.style.height = `${frame.h * zoom}px`;
  }
  const baked = Number(wires.dataset.bakedZoom) || zoom;
  const s = zoom / baked;
  wires.style.transformOrigin = `${-frame.minX * baked}px ${-frame.minY * baked}px`;
  const scale = Math.abs(s - 1) < 0.0001 ? '1' : String(s);
  wires.style.transform = `translate(${camera.x}px, ${camera.y}px) scale(${scale})`;
}

export function settleBoardWires(world, camera) {
  if (!world) return;
  placeWires(world.parentElement?.querySelector('.board-view__wires'), camera, { bake: true });
}

let cameraRecorder = null;

// Minimizing or leaving the window throws the board picture off the graphics card.
// The next drag would have to rebuild it, which is the stick. We rebuild it as
// soon as the window is back, before that drag.
let pictureCold = false;
const WARM_SUFFIX = ' translate3d(0px, 0px, 0px)';

export function markBoardPictureCold() {
  pictureCold = true;
}

/** Upload the card and wire pictures again. A drag that starts during the upload is left alone. */
export function warmBoardPicture(world) {
  if (!pictureCold || !world) return;
  // A focus event can arrive while the window is still minimized. Uploading
  // then would be thrown away, so wait until the window is actually showing.
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  pictureCold = false;
  const wires = world.parentElement?.querySelector('.board-view__wires');
  [world, wires].forEach((layer) => {
    if (!layer) return;
    const transform = layer.style.transform;
    layer.style.willChange = 'auto';
    void layer.offsetWidth;
    layer.style.willChange = 'transform';
    if (!transform || transform.endsWith('translate3d(0px, 0px, 0px)')) return;
    layer.style.transform = `${transform}${WARM_SUFFIX}`;
  });
  requestAnimationFrame(() => {
    [world, wires].forEach((layer) => {
      if (!layer) return;
      const transform = layer.style.transform;
      if (!transform.endsWith('translate3d(0px, 0px, 0px)')) return;
      layer.style.transform = transform.slice(0, -WARM_SUFFIX.length);
    });
  });
}

/** BoardView sets this so a pan is remembered even if the view unmounts mid-drag. */
export function setBoardCameraRecorder(recorder) {
  cameraRecorder = recorder || null;
}

export function place(world, camera) {
  if (!world) return;
  cameraRecorder?.(camera);
  const transform = cameraTransform(camera);
  world.style.transform = transform;
  placeWires(world.parentElement?.querySelector('.board-view__wires'), camera);
  const connection = world.parentElement?.querySelector('.board-view__connection');
  if (connection) connection.style.transform = transform;
  paintDots(world.parentElement?.querySelector('.board-view__dots'), camera);
  world.dispatchEvent(new CustomEvent('panupdate', {
    detail: { x: camera.x, y: camera.y, zoom: camera.zoom },
  }));
}
